import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, ComponentFactoryResolver, ElementRef, EventEmitter, Input, NgZone, OnInit, AfterViewInit, Output, QueryList, ViewChild, ViewChildren, ViewEncapsulation } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { environment } from '../../../../../environments/environment';
import { CurrentUserService } from '../../../../security/current-user.service';
import { GridPasteKendoDialog } from '../../../../shared/dialogs/grid-paste-kendo/grid-paste-kendo.dialog';
import { GeocodeDialog, GeocodeDialogModel } from '../../../dialogs/geocode/geocode-dialog.component';
import { DynamicComponentHostDirective } from '../../../directives/dynamic-content-host.directive';
import { AttachmentStandardColumnNames, GridFlavorEnum } from '../../../enums/grid.enums';
import { IFieldDefinitionLogic } from '../../../interfaces/ifield-definition-logic.interface';
import { FieldDefinition } from '../../../models/form-builder/field-definition.model';
import { FormField } from '../../../models/form-builder/form-field.model';
import { FormInstanceElement } from '../../../models/form-builder/form-instance-element.model';
import { GridFlavorFactory } from '../../../models/form-builder/grid-flavor/grid-flavor-factory';
import { GridConfig } from '../../../models/grid/grid-config.model';
import { GridFilter } from '../../../models/grid/grid-filter.model';
import { GridFormInstanceElementWrapper } from '../../../models/grid/grid-form-instance-element-wrapper.model';
import { GridRowDef } from '../../../models/grid/grid-row.model';
import { CommunicationService } from '../../../services/communication.service';
import { CurrentSiteService } from '../../../services/current-site.service';
import { FieldDefinitionService } from '../../../services/field-definition.service';
import { FormFieldTypeAndNameService } from '../../../services/form-field-type-and-name.service';
import { GeocodeService } from '../../../services/geocode.service';
import { FormFieldBaseComponent } from '../form-field-base/form-field-base.component';
import { GRID_ROW_ID_KEY, GridDesignerEditorBase } from '../grid-field-designer/grid-designer-editor-base';
import { GridEvent } from '../grid-form-field/grid-event';
import { ConfirmationDialogComponent, ConfirmationDialogModel } from '../../../dialogs/confirmation/confirmation-dialog.component';

// BEGIN KENDO GRID IMPORTS.
import { HttpClient } from '@angular/common/http';
import {
    AddEvent,
    CancelEvent,
    CellClickEvent,
    ColumnMenuSettings,
    CommandColumnComponent,
    DetailCollapseEvent,
    DetailExpandEvent,
    GridComponent,
    GridDataResult,
    MultipleSortSettings,
    RemoveEvent,
    RowReorderEvent,
    SaveEvent
} from "@progress/kendo-angular-grid";
import { State } from "@progress/kendo-data-query";
import { AsyncJob } from '../../../models/async-job.model';
import { FormFieldPropertyEnum } from '../../../models/form-builder/form-field-property-enum.model';
import { AsyncJobService } from '../../../services/async-job.service';
import { ExportDataService } from '../../../services/export-data.service';
import { KENDO_DATA_GRID_ROW_KEY, KendoGridService } from '../../../services/kendo-grid.service';
import { DataUsedWithKendoGrid } from './data-used-with-kendo-grid';
// END KENDO GRID IMPORTS. 
import { MonitorExcelImportToGridHelper } from '../../../dialogs/async-job-base/monitor-excel-import-to-gride-helper';
import { ImportGridDataDialog, ImportGridDataDialogInitInfo } from '../../../dialogs/import-grid-data/import-grid-data.dialog';
import { KendoGridRemoteBindingDirective } from '../../../directives/kendo-grid-remote-binding.directive';
import { FormModeEnum } from '../../../enums/form-mode.enum';
import { IGridFlavor } from '../../../interfaces/igrid-flavor.interface';
import { IKendoGridData, IKendoGridDataSpy, KendoGridHelper } from '../../../kendo-grid-helper';
import { GridRowViewModel, IFormContentElement } from '../../../models/form-builder/grid-row.model';
import { ComponentAndFormField } from '../../../models/grid/component-and-form-field-model';
import { FormInstance } from '../../../models/site-content/form-instance.model';
import { TargetFieldConditionalLogicInstructions } from '../../../models/site-metadata/target-field-logic-instructions';
import { FieldConditionalLogicService } from '../../../services/field-conditional-logic.service';
import { FlexibleSelectionFieldService } from '../../../services/flexible-selection-field.service';
import { ImportDataService } from '../../../services/import-data.service';
import { ValueConcatenatorService } from '../../../services/value-concatenator.service';
import { FileInputUtil } from '../../../utility-classes/file-input.util';
import { BehaviorSubject } from 'rxjs';
import { ColumnTotals } from '../../../interfaces/kendo-grid-data-result.interface';
import { FormFieldWrapperComponent } from '../form-field-wrapper/form-field-wrapper.component';
import { GridCellEditorV2DialogManager, GridCellEditorV2Dialog } from '../../../dialogs/grid-cell-editor-v2/grid-cell-editor-v2.dialog';
import { AlertDialogComponent, AlertDialogModel } from '../../../dialogs/alert/alert-dialog.component';

declare let $: any; // jQuery

// Export an output data type.
export class ConditionalLogicColumnDefsFilterData {
    public constructor(public gridComponent: GridComponent, public filteredColumnDefs: FormField[], public childFieldColumnDef: FormField, public showChildField: boolean) {
    }
}

// Implement a class to handle column totals.
interface IMapOfTotalsByCellName {
    [cellName: string]: number;
}
class GridColumnTotalsHelper implements IKendoGridDataSpy {
    // Properties.
    private dataRows: any[] = null;
    private numericColumnDefs: FormField[] = [];
    private cellTotals: IMapOfTotalsByCellName = {};

    // Constructor.
    public constructor(private fieldDefinitionService: FieldDefinitionService, private columnDefs: FormField[]) {
        if (columnDefs != null) {
            for (let index: number = 0; index < columnDefs.length; index++) {
                let columnDef: FormField = columnDefs[index];
                let fieldDef: FieldDefinition = this.fieldDefinitionService.getFieldDefinition(columnDef.fieldDefinitionClassName);
                if (fieldDef.isNumeric)
                    this.numericColumnDefs.push(columnDef);
            }
        }
    }

    // IKendoGridDataSpy method.
    public dataLoaded(loadedData: any[]): void {
        this.dataRows = loadedData;
        this.cellTotals = GridColumnTotalsHelper.calculateAllColumnTotals(this.fieldDefinitionService, this.numericColumnDefs, loadedData);
    }

    public cellValueChanged(rowIndex: number, columnDef: FormField, value: FormInstanceElement): void {
        if ((rowIndex < this.dataRows.length) && (rowIndex >= 0)) {
            let fieldDef: FieldDefinition = this.fieldDefinitionService.getFieldDefinition(columnDef.fieldDefinitionClassName);
            let numericValue: any = value[fieldDef.formInstanceElementPropertyName];
            let rowValues: IMapOfTotalsByCellName = this.dataRows[rowIndex];
            if (rowValues != null)
                rowValues[columnDef.name] = numericValue;

            let total: number = GridColumnTotalsHelper.calculateColumnTotal(columnDef, fieldDef, this.dataRows);
            this.cellTotals[columnDef.name] = total;
        }
    }

    public getFooterValue(columnDef: FormField): string {
        let cellTotal: string = this.cellTotals[columnDef.name] != null ? this.cellTotals[columnDef.name].toString() : '';
        return cellTotal;
    }

    // Helper methods.
    private static calculateAllColumnTotals(fieldDefinitionService: FieldDefinitionService, numericColumnDefs: FormField[], rowData: any[]): IMapOfTotalsByCellName {
        let cellTotals: IMapOfTotalsByCellName = {};

        for (let index: number = 0; index < numericColumnDefs.length; index++) {
            let columnDef: FormField = numericColumnDefs[index];
            let fieldDef: FieldDefinition = fieldDefinitionService.getFieldDefinition(columnDef.fieldDefinitionClassName);
            let total: number = GridColumnTotalsHelper.calculateColumnTotal(columnDef, fieldDef, rowData);
            cellTotals[columnDef.name] = total;
        }
        return cellTotals;
    }
    private static calculateColumnTotal(columnDef: FormField, fieldDef: FieldDefinition, rowData: any[]): number {
        let total: number = 0;

        for (let index: number = 0; index < rowData.length; index++) {
            let row: any = rowData[index];
            let cellValue: any = row[columnDef.name];
            if (cellValue != null) {
                let value: number = fieldDef.customLogicHandler.getNumericValueFrom(cellValue.toString()); //parseFloat(cellValue.toString());
                if (!isNaN(value))
                    total += value;
            }
        }
        return total;
    }
}

// Note:  the following class is used so as not to repeat code in methods ColumnIsFilterable() and IsSortable().
class UserCanFilterResult {
    public constructor(public hasResult: boolean = false, public resultValue: boolean = false) {
    }
}

@Component({
    selector: 'app-grid-field-editor',
    templateUrl: './grid-field-editor.component.html',
    styleUrls: ['../grid-form-field/grid-form-field.component.scss', './grid-field-editor.component.scss'],
    encapsulation: ViewEncapsulation.None, //enable CSS overrides
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    standalone: false
})
export class GridFieldEditorComponent extends GridDesignerEditorBase implements OnInit, AfterViewInit {
    @Input() flavor: GridFlavorEnum = GridFlavorEnum.RegularGrid;
    @Input() formInstance: FormInstance;

    // Properties.
    // This references all of the templates in a row which look like this in the HTML:
    // <ng-template dynamic-component-host name="t-{{gridRow.RowIndex}}-{{hshColumnDef.fieldOrder}}"></ng-template>
    // DO WE STILL NEED THIS?
    @ViewChildren(DynamicComponentHostDirective, { read: DynamicComponentHostDirective }) dynamicComponentHosts: QueryList<DynamicComponentHostDirective>;

    // kendoGridInstance is set inside ngAfterViewInit() which uses kendoGridComponentAccessor below
    private kendoGridInstance: GridComponent;
    // kendoGridComponentAccessor is used to set kendoGridInstance above inside ngAfterViewInit()
    @ViewChildren(GridComponent, { read: GridComponent }) kendoGridComponentAccessor: QueryList<GridComponent>;

    // Get a reference to the remote binding directive that handles all the changes to Grid state (Relates to <kendo-grid #grid="remoteBindingDirectiveRef" in the HTML)
    @ViewChild(KendoGridRemoteBindingDirective) public kendoGridRemoteBindingRef: KendoGridRemoteBindingDirective;

    // Properties.
    // Define instance data used with a MatPaginator.
    @ViewChild('matPaginatorMatFooter', { read: MatPaginator }) matPaginator;

    @Output() editorGridValidation = new EventEmitter();
    @Output() gridEvent = new EventEmitter();
    @Output() editorInitialized = new EventEmitter();

    private newRowIsBeingAdded: boolean = false;

    @Output() conditionalLogicColumnDefsFilter = new EventEmitter();

    //VNEXT-980: KLW - Property to set the number of Kendo grid rows to display
    private rowHeight: number = 23.14;
    private toolbarHeadersFilters = 107; // height of grid with no rows

    private detailRowHeight: number = 0;
    private rowDetailPanel: ElementRef;
    private saving: boolean;
    private extraHeightWhenEditingRow: number = 0;
    // rowCount and gridHasOneUnsavedRow added for VNEXT-2184
    private rowCount: number;
    private gridHasOneUnsavedRow: boolean; // used to detect if has a zero row grid had a row added that was cancelled before saving
    private gridHasBeenEdited: boolean = false;
    private showDoneEditingButton: boolean = false;
    @ViewChild('rowDetailPanel') set content(content: ElementRef) {
        if (content) this.rowDetailPanel = content;
    }
    @ViewChild('selectExcelFile') selectExcelFile; // Used to load an Excel file to import its data.

    public dataUsedWithKendoGrid: DataUsedWithKendoGrid = new DataUsedWithKendoGrid();
    public formInstanceElement: FormInstanceElement;
    public formInstanceElementId: number; // the Id of the FormInstanceElement which contains the grid data
    private helper = new KendoGridHelper();
    private formInstanceId: number;
    private formFieldId: number;
    private showGridFilter: boolean = true;

    @ViewChild('kendoGridComponent') public kendoGridComponent!: GridComponent; // THIS DOES NOT GET SET. USE kendoGridInstance instead

    //TEAMS-838: KLW - Create a form group to be used in validation 
    public formGroup: UntypedFormGroup;
    public validationCount = 0;

    //VNEXT-1540: KLW - Flag for resizable attribute of the Kendo grid
    public resizable: boolean = false;

    private kendoGridService: KendoGridService;
    private gridValidity: string;
    private rowEditInProgress: boolean;

    private filterPresets: GridFilter[] = [];
    public gridFlavorImpl: IGridFlavor;
    public presetFilterSelected: string;
    private isSaveGridRowButtonDisabled: boolean = false;

    // PJH - 11/4/2025 - added for VNEXT-2002 to display totals for all records, not just those currently loaded in the UI
    // columnTotalsSubject gets updates with server values in the handleLoaded() method, whenever counts change. It is subscribed
    // to inside ngOnInit() if ShowNumericTotalsFooter is true, and any updates to it are propagated to columnTotals which is used
    // by getNumericFooterTotalValueFor()
    // See https://community-dc.max.gov/x/vYfJnw for an overview
    private columnTotalsSubject: BehaviorSubject<ColumnTotals> = new BehaviorSubject<ColumnTotals>([]);
    private columnTotals: ColumnTotals = {};

    // PJH - 12/9/2025 - while working on VNEXT-2088 I noticed that while an invalid Date field in a grid row would trigger a red error message, it would not cause
    // a GridRow's save button to become disabled, unlike other fields. It turned out to be because the DateFormFieldComponent gets set up very differently
    // from other fields, including the fact that it has its own FormGroup. As I result, I introduced childComponentsRequiringIndividualValidtional to provide a means
    // to do an additional check for fields' validity where needed
    private childComponentsRequiringIndividualValidtional: FormFieldBaseComponent[] = [];

    // Constructor.
    public constructor(private currentSiteService: CurrentSiteService,
        private flexibleSelectionFieldService: FlexibleSelectionFieldService,
        private fieldValueConcatenatorService: ValueConcatenatorService,
        formFieldTypeAndNameService: FormFieldTypeAndNameService,
        resolver: ComponentFactoryResolver,
        fieldDefinitionService: FieldDefinitionService,
        private dialog: MatDialog,
        private currentUserService: CurrentUserService,
        private exportDataService: ExportDataService,
        private geocodeService: GeocodeService,
        private asyncJobService: AsyncJobService,
        private importDataService: ImportDataService,
        private communicationService: CommunicationService,
        private fieldConditionalLogicService: FieldConditionalLogicService,
        private httpClient: HttpClient,
        private ngZone: NgZone) {
        super(formFieldTypeAndNameService, fieldDefinitionService, resolver);
    }

    // BEGIN ============================================= ADDED FOR SPECIALIZED ATTACHMENTS AND CONTACTS GRIDS ====================================================
    public rebind(): void {
        this.kendoGridRemoteBindingRef?.rebind();
    }

    public get ShowEditingInfo(): boolean {
        if (this.IsReadOnlyGrid) return false;
        return this.gridHasBeenEdited && this.gridFlavorImpl.showEditingInfo;
    }

    public get ShowDoneEditingButton(): boolean {
        return this.showDoneEditingButton;
    }

    public get ShowCancelChangesButton(): boolean {
        const rows = this.kendoGridRemoteBindingRef?.Data?.data ?? [];
        return rows.some(r => r.isEdited || r.isNew || r.id === 0);
    }

    public get RowHeightForVirtualScrolling(): number {
        return this.gridFlavorImpl.rowHeightForVirtualScrolling;
    }

    public get Data(): GridDataResult {
        return this.kendoGridRemoteBindingRef?.Data;
    }

    public get ScrollMode() {
        return this.gridFlavorImpl.scrollMode;
    }

    // TODO: make dataItem typesafe
    public isAttachmentsGridFlavorAndRowIsEnabled(dataItem: any) {
        let retValue: boolean = false;
        if (this.IsAttachmentsGridFlavor) {
            let isEnabled = dataItem?.__gridRow?.hshFormInstanceElementsById[AttachmentStandardColumnNames.IsDisabled]?.standinDisplayValue == "0";
            retValue = isEnabled;
        }
        return retValue;
    }
    // TODO: make dataItem typesafe
    public isAttachmentsGridFlavorAndRowIsDisabled(dataItem: any) {
        let retValue: boolean = false;
        if (this.IsAttachmentsGridFlavor) {
            //console.log('dataItem?.__gridRow?.hshFormInstanceElementsById', dataItem?.__gridRow?.hshFormInstanceElementsById);
            let isDisabled = dataItem?.__gridRow?.hshFormInstanceElementsById[AttachmentStandardColumnNames.IsDisabled]?.standinDisplayValue == "1";
            retValue = isDisabled;
        }
        return retValue;
    }
    public userCanRestore(): boolean {
        return this.formInstance?.canAdmin;
    }
    // Simply emit a GridEvent for the parent to handle
    public emitGridEvent(event: any, type: string) {
        this.gridEvent.emit(new GridEvent(event, type));
    }
    public get IsRegularGridFlavor(): boolean {
        return this.flavor == GridFlavorEnum.RegularGrid;
    }
    public get IsAttachmentsGridFlavor(): boolean {
        return this.flavor == GridFlavorEnum.AttachmentsGrid;
    }
    public get CommandColumnWidth(): number {
        return this.gridFlavorImpl.gridCommandColumnWidth;
    }
    public get ShowAddRowButton(): boolean {
        const field = this.gridFormFieldComponent.FormField;
        return field.userCanAddGridRows && this.IsRegularGridFlavor;
    }
    public get ShowPasteButton(): boolean {
        const field = this.gridFormFieldComponent.FormField;
        return field.userCanImportGridData && this.IsRegularGridFlavor;
    }
    public get ShowUploadFromExcelButton(): boolean {
        const field = this.gridFormFieldComponent.FormField;
        return field.userCanImportGridData && this.IsRegularGridFlavor;
    }
    public get ShowDownloadToExcelButton(): boolean {
        const field = this.gridFormFieldComponent.FormField;
        return field.userCanExportGridData;
    }
    public get ShowClearGridButton(): boolean {
        // Note:  this method relates to ticket https://maxjira.max.gov/browse/VNEXT-2428.
        const field = this.gridFormFieldComponent.FormField;
        if (this.flavor == GridFlavorEnum.RegularGrid) {
            if (field.showLockGridRows) return false;
        }

        return field.userCanDeleteGridRows;

    }
    public get HasNoRows(): boolean {
        return this.rowCount == 0;
    }
    public get ShowRowNumbers(): boolean {
        //VNEXT-1988 ability to disable grid row numbers
        return true;
    }

    public get UserCanEdit(): boolean {
        const field = this.gridFormFieldComponent.FormField;
        return field.userCanEditGridRows;
    }
    // END

    public isPending(dataItem: any): boolean {
        return dataItem.isEdited || dataItem.isNew || dataItem.id == 0;
    }

    private createFormGroup() {
        this.formGroup = new UntypedFormGroup({});
        this.validationCount = 0;
    }

    public get AtLeastOneFieldIsInvalid(): boolean {
        let isInvalid: boolean = false;
        if (this.formGroup) {
            isInvalid = this.formGroup.valid == false;
        }
        if (isInvalid == false) {
            // PJH - 12/9/2025 - while working on VNEXT-2088 I noticed that while an invalid Date field in a grid row would trigger a red error message, it would not cause
            // a GridRow's save button to become disabled, unlike other fields. It turned out to be because the DateFormFieldComponent gets set up very differently
            // from other fields, including the fact that it has its own FormGroup. As a result, I introduced IFieldDefinitionLogic.requiresIndividualValidationInGrids.
            // (See its use in this.gridColumnInit())
            // If the Grid's formGroup is valid, it's possible that one of these components requiring special handling is present, and so we check for that here
            for (const component of this.childComponentsRequiringIndividualValidtional) {
                if (component.FormControl) {
                    isInvalid = component.FormControl.errors != null;
                }
                if (isInvalid == true)
                    break;
            }
        }
        return isInvalid;
    }

    public get FormInstance(): FormInstance {
        return this.formInstance;
    }

    // pharvey - 5/29/2024 - VNEXT-1294 - this is triggered from FormRenderer and CommunicationService when a user clicks the main Save button on a Form
    public saveAnyUnsavedRows(loc: string): Promise<any> {
        if (this.rowEditInProgress) {
            let sender = this.kendoGridInstance;

            let selectedGridRow = this.dataUsedWithKendoGrid.selectedGridRow;
            let rowIndex = this.dataUsedWithKendoGrid.editedRowIndex;
            let isNew = selectedGridRow.DatabaseId == 0;
            if (this.HasFixedRowHeadings) {
                isNew = false;
            }

            let dataItem = this.dataUsedWithKendoGrid.CurrentDataItem;
            let cols = sender.columns['_results'];

            this.newRowIsBeingAdded = false;
            this.rowEditInProgress = false;

            this.saving = true;
            return this.kendoGridService.addOrUpdateRow(
                this.formInstanceElementId,
                this.formInstanceId,
                this.formFieldId,
                selectedGridRow,
                this.formInstance.getSelectedValuesByFieldId(),
                rowIndex,
                cols,
                dataItem,
                isNew,
                this.gridFormFieldComponent.GridConfig,
                this.GridFormFieldComponent,
                sender.data['total']
            ).then(x => {
                this.extraHeightWhenEditingRow = 0;
                this.closeAnyExistingRowEditor(sender, rowIndex);
                this.saving = false;
                this.flagElementAsUpdated();
                this.showDoneEditingButton = false;
                this.gridHasBeenEdited = true;
                if (dataItem) dataItem.isEdited = true;
                return x;
            });
        } else {
            return new Promise<any>((resolve, reject) => {
                resolve(null);
            });
        }
    }

    public handleWrapperFormControlCreated(control: any) {
        // pharvey - if a control has a status of "DISABLED" it means it's readonly, so skip it to avoid spurious validation errors
        if (this.formGroup && control.status != FormFieldPropertyEnum.DISABLED) {
            this.formGroup.addControl("KendoValidation" + this.validationCount.toString(), control);
            this.validationCount++;
        }
    }

    // PJH - 7/30/2025 - VNEXT-1867 - for form save optimizations need to be able to flag a grid has having been updated when it fixed row headings have been added to it by kendo grid service
    // There is new code on the server and in the client to set and propagate a new property "flagGridRowsValueAsChanged" which is used here to set the grid's FormInstanceElement's
    // gridRowsValueHasChanged property
    public handleLoaded(event: IKendoGridData) {
        this.rowCount = event.count;
        this.formInstanceElement.gridRowsValueHasChanged = event.gridData.flagGridRowsValueAsChanged;
        this.formInstanceElement.UserUpdatedData = event.gridData.flagGridRowsValueAsChanged;
        this.helper.handleLoaded(event);
        this.columnTotalsSubject.next(event.columnTotals);

        this.gridEvent.emit(new GridEvent(event, 'gridLoaded'));
    }

    // Life cycle methods.
    public ngOnInit(): void {
        this.gridFlavorImpl = GridFlavorFactory.getImplementation(this.flavor);

        this.communicationService.registerGrid(this);

        // LOOK INTO GETTING THIS INJECTED AT THE COMPONENT LEVEL (look at providers) SO WE DON"T HAVE TO MANUALLY INSTANTIATE IT HERE
        this.kendoGridService = new KendoGridService(this.httpClient, this.fieldDefinitionService, this.flexibleSelectionFieldService, this.fieldValueConcatenatorService);
        // TO DO:  MOVE 'preview'/'instance' mode-related ngOnInit() code from grid-form-field.component.ts. to this method.

        // 05-02-2024 note:  added the following code to handle grid column totals, if so configured.
        if (this.ShowNumericTotalsFooter) {
            // PJH - 11/4/2025 - added for VNEXT-2002 to display totals for all records, not just those currently loaded in the UI
            this.columnTotalsSubject.subscribe(x => {
                this.columnTotals = x;
            });
        }

        this.adjustToolbarHeadersFiltersHeight();

        //apply preset filters
        if (this.Mode != FormModeEnum.PREVIEW) { //form instance is null in design mode
            let gridField = this.formInstance.form.formFields.find(g => g.id == this.gridFormFieldComponent.FormField.id);
            if (gridField) {
                let filterPresetsJson: GridFilter[] = JSON.parse(gridField.formTemplateInstancesGridJsonConfig);
                if (filterPresetsJson) {
                    let filters = filterPresetsJson["presetGridFilters"];
                    if (filters && filters[0].filterName) {
                        this.filterPresets = filters;
                    }
                }
            }
        }
        this.editorInitialized.emit(this);
    }

    public ngAfterViewInit(): void {
        this.kendoGridComponentAccessor.changes.subscribe((changes: QueryList<GridComponent>) => {
            this.kendoGridInstance = changes.first; // it's safe to use .first because we know there'll only be one of them
        });
        this.hideGridBodyIfSoConfigured();
    }

    // Called when adding a new row or clicking an existing row
    public gridColumnInit(hshEventData: any, hshUnusedColumnDef: FormField): void {
        super.gridColumnInit(hshEventData, hshUnusedColumnDef);

        let wrapper: FormFieldWrapperComponent = hshEventData['component'];

        if (wrapper != null) {
            // PJH - 12/9/2025 - while working on VNEXT-2088 I noticed that while an invalid Date field in a grid row would trigger a red error message, it would not cause
            // a GridRow's save button to become disabled, unlike other fields. It turned out to be because the DateFormFieldComponent gets set up very differently
            // from other fields, including the fact that it has its own FormGroup. As I result, I introduced IFieldDefinitionLogic.requiresIndividualValidationInGrids.
            let fieldDefClientLogic: IFieldDefinitionLogic = this.fieldDefinitionService.getFieldClientLogicHandler(wrapper.FormField.fieldDefinitionClassName);
            if (fieldDefClientLogic.requiresIndividualValidationInGrids && this.childComponentsRequiringIndividualValidtional.indexOf(wrapper.WrappedFormComponent) == -1) {
                this.childComponentsRequiringIndividualValidtional.push(wrapper.WrappedFormComponent);
            }
            // Begin 11-14-2024 new code (to work around not using [(ngMode)] anymore):
            let componentFormField = wrapper.FormField;
            let componentFormInstanceElement = wrapper.FormInstanceElement;
            let cellFormInstanceElementWrapper = this.DataUsedWithKendoGrid.selectedGridRow.getFormInstanceElementWrapper(componentFormField);
            if ((componentFormInstanceElement != null) && (cellFormInstanceElementWrapper != null) && (cellFormInstanceElementWrapper.formInstanceElement != null)) {
                componentFormInstanceElement.copyWithAnyChildElements(cellFormInstanceElementWrapper.formInstanceElement);
            }
            // End 11-14-2024 new code.
        }

    }

    // Override applicable base class/FormFieldComponent methods.
    public formFieldUpdated(): void {
        // This methdd is called when a conditional logic change is applied to the grid as a whole,
        // As such, it makes sense to call the server to reload the grid's data, particularly if
        // the grid is being shown from a hidden state.  If this method needs to be optimized, so
        // as not to call the server unnecessarily, a parameter or parameters would need to be
        // passed to this method to allow the receiver to know if the target field, in this case
        // a grid, is being shown from a hidden state.
        this.rebind();
    }

    public isCompoundObjectComponent(): boolean {
        // Note:  this method is also used in apply conditional logic.
        return true;
    }
    public resetConditionalLogicSettings(): void {
        // Note:  reset cached column settings.
        this.gridFormFieldComponent.GridConfig.ConditionalLogicColumnDefs = null;
    }
    public applyChildFieldAttributes(childFieldName: string, showChildField: boolean, childFieldIsReadOnly: boolean, childFieldIsRequired: boolean): void {
        const gridConfig = this.gridFormFieldComponent?.GridConfig;
        if (!gridConfig) return;

        const childFieldColumnDef = gridConfig.ColumnDefs.find(cd => cd.name === childFieldName);
        if (!childFieldColumnDef) return;

        childFieldColumnDef.readOnly = childFieldIsReadOnly;
        childFieldColumnDef.required = childFieldIsRequired;

        let columnDefsToFilter: FormField[] = gridConfig.CachedGridColumnDefs != null ? gridConfig.CachedGridColumnDefs : gridConfig.ColumnDefs;
        // Note:  if the target column is not already included in columnDefsToFilter, we need to add it.
        let filteredColumnDefs: FormField[] = null;
        if (showChildField && (columnDefsToFilter.find(cd => cd.name == childFieldColumnDef.name) == null)) {
            columnDefsToFilter.push(childFieldColumnDef);
            filteredColumnDefs = columnDefsToFilter;
        } else if ((!showChildField) && (columnDefsToFilter.find(cd => cd.name == childFieldColumnDef.name) != null)) {
            filteredColumnDefs = columnDefsToFilter.filter(cd => cd.name != childFieldColumnDef.name);
            // childFieldColumnDef.doNotDisplayInGrid = true; // 02-11-2026 note:  disabling this fix until there is time to test it.
        } else {
            filteredColumnDefs = columnDefsToFilter;
        }

        let outputData = new ConditionalLogicColumnDefsFilterData(this.kendoGridComponent, filteredColumnDefs, childFieldColumnDef, showChildField);
        this.conditionalLogicColumnDefsFilter.emit(outputData);

        if ((this.gridFormFieldComponent != null) && (this.gridFormFieldComponent.RuntimeMetadata != null) && (this.gridFormFieldComponent.RuntimeMetadata.AllComponents != null)) {
            for (let index: number = 0; index < this.gridFormFieldComponent.RuntimeMetadata.AllComponents.length; index++) {
                let componentInfo: ComponentAndFormField = this.gridFormFieldComponent.RuntimeMetadata.AllComponents[index];
                if (childFieldColumnDef.id == componentInfo.FormField.id) {
                    componentInfo.Component.formFieldUpdated();
                }
            }
        }
    }

    private applyFieldConditionalLogicInstructions(targetFieldInstructions: TargetFieldConditionalLogicInstructions[], resetCachedColumns: boolean = true): void {
        // Clear any cached/saved column definitions.
        if (resetCachedColumns)
            this.gridFormFieldComponent.GridConfig.CachedGridColumnDefs = null;

        // Apply any instructions.
        if (targetFieldInstructions != null) {
            let instructionsForFormRenderer: TargetFieldConditionalLogicInstructions[] = [];

            for (let index: number = 0; index < targetFieldInstructions.length; index++) {
                let targetFieldInstruction: TargetFieldConditionalLogicInstructions = targetFieldInstructions[index];

                if (targetFieldInstruction.targetFieldId == this.gridFormFieldComponent.FormField.id) {
                    this.applyChildFieldAttributes(targetFieldInstruction.childFieldName, targetFieldInstruction.showTargetField, !targetFieldInstruction.makeTargetFieldEditable, targetFieldInstruction.makeTargetFieldRequired);
                } else {
                    instructionsForFormRenderer.push(targetFieldInstruction);
                }
            }

            if (instructionsForFormRenderer.length > 0) {
                // Note:  these are instructions that the form renderer, my parent, has to apply, so let my
                //        parent know that there are conditional logic instructions that need to be applied.
                this.gridFormFieldComponent.conditionalLogicInstructions.emit(instructionsForFormRenderer);
            }
        }
    }

    // Define accessor methods called by my HTML code.
    public get Helper(): KendoGridHelper {
        return this.helper;
    }

    public get IsLoading() {
        return this.kendoGridService.Loading;
    }

    public get HideRowDelete(): boolean {
        return this.gridFormFieldComponent.FormField.disableGridRowDeleteForNonAdmins && (!this.currentSiteService.Site.siteIsAdministerable);
    }

    // For a row to be locked 1) "Allow locking of grid rows"" has to be turned on for the Grid and 2) the row's IsLocked flag has to be true
    public RowIsLocked(dataItem: any): boolean {
        return this.gridFormFieldComponent.FormField.showLockGridRows &&
        dataItem.isLocked;
    }

    public ShowCellContentsOpenIcon(dataItem: any, columnDef: FormField): boolean {
        if (!this.RowIsLocked(dataItem)) {
            return false;
        } else {
            let handler: IFieldDefinitionLogic = this.fieldDefinitionService.getFieldDefinition(columnDef.fieldDefinitionClassName).customLogicHandler;
            return handler.supportsViewingCellContentsInDialog;
        }
    }

    public get IsReadOnlyGrid() {
        const field = this.gridFormFieldComponent.FormField;
        return !field.userCanEditGridRows || this.ReadOnly;
    }

    public get ModifyRowDisabled() {
        return this.ReadOnly;

    }

    public get HasFixedRowHeadings() {
        return this.gridFormFieldComponent.FormField.fixedFirstColumnJson != null;
    }

    public get HideFormFieldBody(): boolean {
        return this.gridFormFieldComponent.FormField.hideFormFieldBody;
    }

    public parentReceivedFormInstanceElement(formInstanceElement: FormInstanceElement): void {
        this.formInstanceElement = formInstanceElement;
        this.formInstanceElementId = formInstanceElement.id;
        this.formFieldId = formInstanceElement.formFieldId;
        this.formInstanceId = formInstanceElement.formInstanceId;
    }

    public getNumericFooterTotalValueFor(colIndex: number): number {
        if (this.columnTotals) {
            return this.columnTotals[colIndex];
        } else {
            return 0;
        }
    }

    public get FilterPresets(): GridFilter[] {
        return this.filterPresets;
    }

    public get IsSaveGridRowButtonDisabled(): boolean {
        return this.isSaveGridRowButtonDisabled;
    }

    public selectPresetFilter(e): void {
        this.helper.handleClearFilters();
        if (e?.filters) {
            e.filters.forEach(f => {
                this.helper.Filter.filters.push({
                    field: f.columnName, // 'c1',
                    operator: f.filterOperatorAbbreviation, //'eq',
                    value: f.filterMatchValue // 'Agency A'
                })
            })
            this.helper.Filter.logic = "and";
        }
    }

    public filterChange(): void {
        this.presetFilterSelected = "";
    }

    public ShowGeocodeGrid(): boolean {
        return this.gridFlavorImpl.showGeocodeGrid(environment.apiUrl.toLowerCase(), this.FormFieldId);
    }

    public geoCodeGrid(): void {
        let gridCols = [];
        this.GridColumnDefs.forEach(gcd => {
            gridCols.push({ id: gcd.id, name: gcd.name });
        })

        let dialogRef = this.dialog.open(GeocodeDialog, {
            width: "500px",
            data: new GeocodeDialogModel(
                `Geocode Grid Data`,
                gridCols
            )
        });

        dialogRef.afterClosed().subscribe(retVal => {
            if (retVal) {
                this.geocodeService.GeocodeGrid(this.formInstanceId, this.formInstanceElementId).then(resp => {
                    this.flagDirty();
                    this.kendoGridService.read(this.gridFormFieldComponent.GridConfig, this.fieldDefinitionService, this.gridFormFieldComponent, this.formInstanceElementId, this.dataUsedWithKendoGrid.gridState);
                });
            }
        });
    }

    public showPasteAreaKendo(): void {
        // Show dialog for pasted Excel data.
        let dialogConfig: MatDialogConfig = new MatDialogConfig();
        dialogConfig.hasBackdrop = true;
        dialogConfig.width = '600px';

        let gridHasData: boolean = false;

        dialogConfig.data = { colCount: this.gridFormFieldComponent.GridConfig.ColumnCount, rowCount: this.Helper.TotalRowCount, gridHasData: this.Helper.TotalRowCount > 0, pasteMode: 'replace', pasteData: '' };
        const dialogRef = this.dialog.open(GridPasteKendoDialog, dialogConfig);
        dialogRef.disableClose = true;
        dialogRef.beforeClosed().subscribe(() => dialogRef.close(dialogConfig.data));
        dialogRef.afterClosed().subscribe(replaceGridData => {
            if ((replaceGridData != null) && (replaceGridData.pasteData != null) && (replaceGridData.pasteData.trim() != '')) {
                let replaceGridDataFlag: boolean = replaceGridData.pasteMode == 'replace';
                this.importDataService.importExcelPasteDataIntoGrid(replaceGridData.pasteData, this.formInstanceElementId, replaceGridDataFlag).then(asyncJob => {
                    let jobMonitor: MonitorExcelImportToGridHelper = new MonitorExcelImportToGridHelper(this.asyncJobService);
                    jobMonitor.startMonitoringJob(asyncJob, this.jobCompletedOrUpdatedArrowFunction);

                    let dialogInitInfo = new ImportGridDataDialogInitInfo(this.formInstanceElementId, null, null, asyncJob, replaceGridDataFlag);

                    let dialogConfig: MatDialogConfig = new MatDialogConfig();
                    dialogConfig.hasBackdrop = true;
                    dialogConfig.width = '500px';
                    dialogConfig.height = '590px';
                    dialogConfig.data = dialogInitInfo;

                    const dialogRef = this.dialog.open(ImportGridDataDialog, dialogConfig);

                    dialogRef.afterClosed().subscribe(result => {
                        // If the result is non-null, reload the grid's data.
                        if (result != null) {
                            this.flagDirty(); // Indicate that there is new data that has not yet been saved.
                            this.kendoGridService.readPending(
                                this.gridFormFieldComponent.GridConfig,
                                this.fieldDefinitionService,
                                this.gridFormFieldComponent,
                                this.formInstanceElementId,
                                this.dataUsedWithKendoGrid.gridState
                            );
                        }
                    });

                });
            }
        });

        return;
    }

    public importGridDataClicked(): void {
        this.selectExcelFile.nativeElement.click();
    }
    public exportGridDataClicked(): void {
        this.exportDataService.exportGridDataAsExcelSpreadsheet(this.FormInstanceElementId).then(asyncJob => {
            this.asyncJobService.monitorJobUntilCompletion(asyncJob, 250, this.openExportedDataExcelSpreadsheet);
        });
    }

    public clearGridDataClicked(): void {
        const formField: FormField = this.gridFormFieldComponent.FormField;
        const gridName: string = (formField.displayName != null) && (formField.displayName.trim() != '') ? formField.displayName : formField.name;

        let dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: new ConfirmationDialogModel(
                `Clear ${gridName} Data`,
                `Are you sure you want to clear/remove the rows for this grid?`)
        });
        dialogRef.afterClosed().subscribe(yes => {
            if (yes) {
                this.kendoGridService.clearGridData(this.FormInstanceElementId).then(result => {
                    // Reload to show that there is no data.
                    //
                    // Note:  the user still needs to click save for the form instance to delete the rows.
                    this.rebind();
                });
            }
        });
    }

    // PJH - ready to use for VNEXT-1798
    // (NOTE current version is hard coded in the HTML template)
    public exportGridHistoryDataClicked(version: number) {
        this.exportDataService.exportGridHistoryDataAsExcelSpreadsheet(this.FormInstanceElementId, version).then(asyncJob => {
            this.asyncJobService.monitorJobUntilCompletion(asyncJob, 250, this.openExportedDataExcelSpreadsheet);
        });
    }

    private openExportedDataExcelSpreadsheet = (asyncJob: AsyncJob, successFlag: boolean) => {
        if (successFlag == true) {
            let downloadFileName: string = `grid_${this.FormInstanceElementId}_data.xlsx`;
            let downloadFileURL: string = this.exportDataService.getResultFileDownloadUrl(asyncJob, downloadFileName);

            window.open(downloadFileURL, '_blank');
        } else {
            // We need to report to the user that an error has occurred.
            const message: string = `An error occurred with grid data export/download job ${asyncJob.jobKey}.`;
            const correlationId: string = null; // Note:  when possible, try to extract this value from the asyncJob instance.
            this.dialog.open(AlertDialogComponent, {
                disableClose: true,
                maxWidth: "600px",
                data: new AlertDialogModel("An error occurred", message, null, correlationId)
            });
        }
    }

    public onExcelFileChanged(eventData: any): void {
        if (this.selectExcelFile.nativeElement.files?.length > 0) {
            let file: File = this.selectExcelFile.nativeElement.files[0];
            let filename: string = file.name;

            let asyncJob: AsyncJob = null;

            let dialogInitInfo = new ImportGridDataDialogInitInfo(this.formInstanceElementId, file, filename, asyncJob);

            let dialogConfig: MatDialogConfig = new MatDialogConfig();
            dialogConfig.hasBackdrop = true;
            dialogConfig.width = '500px';
            dialogConfig.height = '645px';
            dialogConfig.data = dialogInitInfo;

            const dialogRef = this.dialog.open(ImportGridDataDialog, dialogConfig);

            dialogRef.afterClosed().subscribe(result => {
                // Always clear the file control.  If the grid was updated, indicated by a non-null result, refresh the grid.
                FileInputUtil.clearFileSelection(this.selectExcelFile);
                if (result != null) {
                    this.flagDirty(); // Indicate that there is new data that has not yet been saved.
                    this.formInstanceElement.gridRowsValueHasChanged = true;
                    this.formInstanceElement.UserUpdatedData = true;
                    this.kendoGridService.readPending(
                        this.gridFormFieldComponent.GridConfig,
                        this.fieldDefinitionService,
                        this.gridFormFieldComponent,
                        this.formInstanceElementId,
                        this.dataUsedWithKendoGrid.gridState
                    );
                }
            });
        }
    }

    public SortSettings: MultipleSortSettings = {
        mode: "multiple",
        initialDirection: "desc",
        allowUnsort: true,
        showIndexes: true,
    };

    public ColumnMenuSettings: ColumnMenuSettings = {
        view: 'tabbed',
        lock: true,
        stick: true,
        setColumnPosition: { expanded: true },
        autoSizeColumn: true,
        autoSizeAllColumns: true
    };

    public get GridIsFilterable(): boolean {
        const field = this.gridFormFieldComponent.FormField;
        return field.userCanFilterGrids && !field.hideGridFiltering;
    }

    public FilterType(columnDef: FormField): string {
        let handler: IFieldDefinitionLogic = this.fieldDefinitionService.getFieldDefinition(columnDef.fieldDefinitionClassName).customLogicHandler;
        let filterType = handler.filterType();
        return filterType;
    }

    public turnOnGridFilter() {
        this.showGridFilter = true;
    }

    public turnOffGridFilter() {
        this.Helper.handleClearFilters();
        this.presetFilterSelected = '';
        this.showGridFilter = false;
    }

    public ColumnIsFilterable(columnDef: FormField): boolean {
        // This is temporary conditional logic.
        // The isFilterable() method of Multi value fieldDefs now returns "true" in order for them to enabled on the home page grids, but they are still not enabled on
        // regular grids yet

        if (!this.showGridFilter)
            return false;

        // Is filtering and sorting turned off for the entire grid?
        /*
        if ((this.currentSiteService.Site != null) && (this.currentSiteService.Site.siteIsAdministerable))
            return true;
        else if ((this.formInstance != null) && (this.formInstance.ignoreAnyFieldLevelRestrictions == true))
            return true;
        else if ((this.gridFormFieldComponent != null) && (this.gridFormFieldComponent.FormField != null)) {
            if (this.gridFormFieldComponent.FormField.hideGridFiltering)
                return false;
            else if (this.gridFormFieldComponent.FormField.userCanFilterGrids == true)
                return true;
        }
        */
        let filterResult = this.tryToDeriveCanFilterAndSortResult();
        if (filterResult.hasResult == true)
            return filterResult.resultValue;

        if (columnDef.fieldDefinitionClassName.indexOf('MultiDropDownFieldDefinition') > -1 || columnDef.fieldDefinitionClassName.indexOf('MultiCheckBoxFieldDefinition') > -1) {
            return false;
        }
        let handler: IFieldDefinitionLogic = this.fieldDefinitionService.getFieldDefinition(columnDef.fieldDefinitionClassName).customLogicHandler;
        return handler.isFilterable();
    }

    public get ShowGridFilter(): boolean {
        return this.showGridFilter;
    }

    public IsSortable(columnDef: FormField): boolean {
        // Is filtering and sorting turned off for the entire grid?
        /*
        if ((this.currentSiteService.Site != null) && (this.currentSiteService.Site.siteIsAdministerable))
            return true;
        else if ((this.formInstance != null) && (this.formInstance.ignoreAnyFieldLevelRestrictions == true))
            return true;
        if ((this.gridFormFieldComponent != null) && (this.gridFormFieldComponent.FormField != null)) {
            if (this.gridFormFieldComponent.FormField.hideGridFiltering)
                return false;
        }
        */
        let filterResult = this.tryToDeriveCanFilterAndSortResult();
        if (filterResult.hasResult == true)
            return filterResult.resultValue;

        if (columnDef.fieldDefinitionClassName.indexOf('MultiDropDownFieldDefinition') > -1 || columnDef.fieldDefinitionClassName.indexOf('MultiCheckBoxFieldDefinition') > -1) {
            return false;
        }
        let handler: IFieldDefinitionLogic = this.fieldDefinitionService.getFieldDefinition(columnDef.fieldDefinitionClassName).customLogicHandler;
        return handler.isFilterable();
    }

    private tryToDeriveCanFilterAndSortResult(): UserCanFilterResult {
        let result = new UserCanFilterResult();

        // Check a number of conditions to see if the grid can be filtered and sorted.
        // Is filtering and sorting turned off for the entire grid?
        if ((this.currentSiteService.Site != null) && (this.currentSiteService.Site.siteIsAdministerable)) {
            result.hasResult = true;

            result.resultValue = true;
        }
        // 09-23-2026 note:  the grid's "Always Hide Grid Filtering" check box overrides the "Ignore Any Grid Restrictions" permission.
        else if ((this.gridFormFieldComponent != null) && (this.gridFormFieldComponent.FormField != null) && this.gridFormFieldComponent.FormField.hideGridFiltering) {
            result.hasResult = true;

            result.resultValue = false;
        }
        else if ((this.formInstance != null) && (this.formInstance.ignoreAnyFieldLevelRestrictions == true)) {
            result.hasResult = true;

            result.resultValue = true;
        }
        else if ((this.gridFormFieldComponent != null) && (this.gridFormFieldComponent.FormField != null)) {
            /*
            if (this.gridFormFieldComponent.FormField.hideGridFiltering) {
                result.hasResult = true;

                result.resultValue = false;
            }
            */
            if (this.gridFormFieldComponent.FormField.userCanFilterGrids != null) {
                result.hasResult = true;

                result.resultValue = this.gridFormFieldComponent.FormField.userCanFilterGrids; //true;
            }
        } 

        return result;
    }

    public GetColumnWidth(columnDef: FormField): number {
        return columnDef.gridColumnWidth > 0 ? columnDef.gridColumnWidth : 180;
    }

    // Determines if the "+" sign should be shown to allow the row to be expanded and show fields that are configured to display in the detail area
    public GridHasDetail(): boolean {
        let res = this.GridColumnDefs.filter(x => { return ['ShowInExpandedDetailOnly', 'ShowInRowDataAndDetail'].indexOf(x.gridColumnBehavior) > -1 });
        return res.length > 0;
    }

    public ShowColumnInRow(columnDef: FormField): boolean {
        return !columnDef.gridColumnBehavior || (['ShowInRowDataOnly', 'ShowInRowDataAndDetail'].indexOf(columnDef.gridColumnBehavior) > -1);
    }

    public ShowColumnInDetail(columnDef: FormField): boolean {
        return ['ShowInExpandedDetailOnly', 'ShowInRowDataAndDetail'].indexOf(columnDef.gridColumnBehavior) > -1;
    }

    public ShowColumnAs(columnDef: FormField): string {
        let logicHandler: IFieldDefinitionLogic = this.fieldDefinitionService.getFieldDefinition(columnDef.fieldDefinitionClassName).customLogicHandler;
        return logicHandler.whenInGridShowAs();
    }

    //VNEXT-894: KLW - Needed to show the contents of the HTML link form field as a URL
    public ShowColumnAsURLLink(columnDef: FormField): boolean {
        let retVal = false;

        let handler: IFieldDefinitionLogic = this.fieldDefinitionService.getFieldDefinition(columnDef.fieldDefinitionClassName).customLogicHandler;

        if (handler) {
            if (handler.isURLLink()) {
                retVal = true
            }
        }
        return retVal;
    }

    // TODO: It should be ok to remove this -- it's no longer being used since the KendoGridRemoteBinding directive now manages state updates
    public onStateChange(state: State): void {
        this.dataUsedWithKendoGrid.gridState = state;
    }

    // Handles clicking the "Add Row" button
    public addRowToUI({ sender }: AddEvent): void {
        this.kendoGridInstance = sender;
        this.flexibleSelectionFieldService.getFlexibleSelectFieldInstructionsForGridRow(this.formInstanceId, null /*rowData*/, this.GridColumnDefs, this.formInstance);

        this.extraHeightWhenEditingRow = 100; // TODO - set it by calculating the actual row height
        this.closeAnyExistingRowEditor(sender);
        this.rowEditInProgress = true;

        // in order for the new row to display correctly, need to construct a GridRowDef and set it as this.DataUsedWithKendoGrid.selectedGridRow
        // (This enables the two-way databinding to work in the template ... [(ngModel)]="this.DataUsedWithKendoGrid.selectedGridRow?.FormInstanceElementWrappers[iColIndex].formInstanceElement")
        let gridRow = new GridRowDef(this.gridFormFieldComponent.GridConfig, 0, 0, 0, 0, this.GridFormFieldComponent);
        this.DataUsedWithKendoGrid.selectedGridRow = gridRow;

        // GridComponent.addRow() expects a FormGroup so create one (maybe the Kendo GridComponent can provide this?)
        let formFields = {}
        for (let colDef of this.GridColumnDefs) {
            formFields[colDef.displayName] = new UntypedFormControl();
        }
        let fg = new UntypedFormGroup(formFields);

        this.flagDirty();

        sender.addRow(fg);
        // If we're adding a row to a zero row grid set gridHasOneUnsavedRow so we can handle a cancel properly with regard to validation
        if (this.rowCount == 0) {
            this.gridHasOneUnsavedRow = true;
        } else {
            this.gridHasOneUnsavedRow = false;
        }
        this.rowCount += 1;
        this.gridHasBeenEdited = true;
        this.showDoneEditingButton = true;

        // this.gridFormFieldComponent.ignoreAnySavedValidationState = true;
    }

    /**
     * Used to call a service method which will remove a row from a grid.
     */
    public handleRemove(event: RemoveEvent): void {
        this.gridFormFieldComponent.FormInstanceElement.UserUpdatedData = true;
        this.flagElementAsUpdated();
        this.kendoGridService.removeRow(event.dataItem['__gridRow'].databaseId, this.flavor).then(x => {
            this.rowCount = x;
            this.gridFormFieldComponent.CheckFieldForValidation();
            this.gridHasBeenEdited = true;
        });
    }

    // Certain field types when clicked shouldn't place the row they're in into edit mode (for example url builder button fields)
    private doNotEnterEditMode(columnIndex) {
        let colDef = this.GridColumnDefs[columnIndex];
        if (colDef) {
            let fieldLogic = this.fieldDefinitionService.getFieldClientLogicHandler(colDef.fieldDefinitionClassName);
            return (fieldLogic?.doNotTriggerGridRowEditFor());
        } else {
            return false;
        }
    }

    public handleEditCellClick(event: CellClickEvent) {
        if (this.RowIsLocked(event.dataItem)) return; //VNEXT-2372
        if (this.doNotEnterEditMode(event.columnIndex - 1)) return;
        // effectively disables editing
        if (!this.rowUsesButton('edit', event.dataItem)) return;

        this.gridHasBeenEdited = true; // not strictly true but hey
        this.showDoneEditingButton = true;

        let isHTMLColumn: FormField;
        let handler: IFieldDefinitionLogic;
        this.DataUsedWithKendoGrid.CurrentDataItem = event.dataItem;

        //VNEXT-894: KLW - Handle if the data is null and if not account if the form field is a URL
        if (event.columnIndex > 0 && event.dataItem?.__gridRow?.gridConfig) {
            isHTMLColumn = event.dataItem.__gridRow.gridConfig.columnDefs[event.columnIndex - 1];

            if (isHTMLColumn) {
                handler = this.fieldDefinitionService
                    .getFieldDefinition(isHTMLColumn.fieldDefinitionClassName)
                    .customLogicHandler;
                if (handler.isURLLink()) return;
            }
        }

        // let handlers for specific command events (like edit, remove, update etc) handle these events
        if (event.column instanceof CommandColumnComponent || event.rowIndex < 0) return;

        this.handleEdit({
            sender: event.sender,
            rowIndex: event.rowIndex,
            columnIndex: event.columnIndex,
            dataItem: event.dataItem,
            isEdited: event.isEdited
        });
    }

    // Handles when user starts editing a row
    // If a row is open and has been edited, this method saves that one first
    // In all paths it handles, it goes on to call thisd.setupRowForEditing()
    public handleEdit({ sender, rowIndex, columnIndex, dataItem, isEdited }): void {
        this.resizable = true;

        let databaseId = dataItem.__gridRow.databaseId;

        this.extraHeightWhenEditingRow = 100;
        if (this.rowEditInProgress) {
            let editedRowIndex = this.dataUsedWithKendoGrid.editedRowIndex;
            // Kendo Grid fires its cellClick event when elements inside a Grid are clicked. That means clicking, for example, a dropdown or radio button fires the event
            // The next line handles that. May need to look to consume the click event in our own form field components (radio, dropdown etc.)
            if (editedRowIndex == rowIndex) return;

            if (this.gridIsDirty()) {
                // save the row that was being edited...
                let dataItemToSave = sender.data.data[editedRowIndex]; // sender.data is an object one of whose properties is data, the array of DataItems
                let cols = sender.columns['_results'];
                this.saving = true;

                this.kendoGridService.addOrUpdateRow(
                    this.formInstanceElementId,
                    this.formInstanceId,
                    this.formFieldId,
                    this.dataUsedWithKendoGrid.selectedGridRow,
                    this.formInstance.getSelectedValuesByFieldId(),
                    editedRowIndex,
                    cols,
                    dataItemToSave,
                    false,
                    this.gridFormFieldComponent.GridConfig,
                    this.GridFormFieldComponent)
                    .then(x => {
                        this.flagElementAsUpdated();
                        this.closeAnyExistingRowEditor(sender, editedRowIndex);
                        this.saving = false;
                        if (rowIndex != editedRowIndex) {
                            this.setupRowForEditing(dataItem, rowIndex, databaseId, sender);
                        }
                        this.gridHasBeenEdited = true;
                        dataItemToSave.isEdited = true;
                    });
            } else {
                // close the row
                this.closeAnyExistingRowEditor(sender, editedRowIndex);
                this.setupRowForEditing(dataItem, rowIndex, databaseId, sender);
            }
        } else {
            this.setupRowForEditing(dataItem, rowIndex, databaseId, sender);
        }

        //VNEXT-1540: KLW - Code to set resizable to true when editing
        this.fitColumns();
    }

    // PJH - 7/31/2025 - VNEXT-1867 - added to flag the field's FormInstanceElement as having been updated
    // when rows are added/edited/deleted
    // TODO: since there is now this.formInstanceElement we can just use that rather than look it up
    public flagElementAsUpdated() {
        let el = this.formInstance.formInstanceElements.find(x => { return x.id == this.formInstanceElementId; });
        if (el) {
            el.gridRowsValueHasChanged = true; // possibly I don't need this if I have UserUpdatedData?
            el.UserUpdatedData = true;
        }
    }

    //VNEXT-894: KLW - These methods are needed for validation on a new Kendo grid row
    public addValidationAndFlagForNewKendoRow() {
        this.createFormGroup();
        this.newRowIsBeingAdded = true;
    }

    public removeNewKendoRowFlag() {
        this.createFormGroup();
        this.newRowIsBeingAdded = false;
    }

    public rowUsesButton(button: string, dataItem: any): boolean {
        const field = this.gridFormFieldComponent.FormField;

        if (button == 'edit' && !field.userCanEditGridRows) {
            return false;
        } else if (button == 'delete' && !field.userCanDeleteGridRows) {
            return false;
        }

        return this.gridFlavorImpl.usesButton(button, dataItem);
    }

    public get HasRows(): boolean {
        return this.rowCount > 0;
    }

    public get RowCount(): number {
        return this.rowCount;
    }
    public set RowCount(value: number) {
        this.rowCount = value;
    }

    public get NewRowDisabled(): boolean {
        if (this.HasFixedRowHeadings) return true;
        if (this.newRowIsBeingAdded) return true;
        return !this.gridFlavorImpl.canAddNewRow;
    }

    // Very rudimentary initial, quick stab at adjustment of the value of this.toolbarHeadersFilters to account for
    // wrapping grid column headers. Really, this should also take into account 1) the number of columns and
    // 2) the width of the component
    private adjustToolbarHeadersFiltersHeight() {
        let gridHeaderText = this.GridColumnDefs.map(x => x.displayName).filter(x => { return x != null });
        let longestHeader = gridHeaderText.reduce(function (a, b) { return a.length > b.length ? a : b; }, '');
        if (longestHeader.length > 25) {
            this.toolbarHeadersFilters += longestHeader.length;
        }
    }

    private setupRowForEditing(dataItem: any, rowIndex: number, databaseId: number, sender: any) {
        this.flexibleSelectionFieldService.getFlexibleSelectFieldInstructionsForGridRow(this.formInstanceId, dataItem, this.GridColumnDefs, this.formInstance);
        this.rowEditInProgress = true;
        this.dataUsedWithKendoGrid.editedRowIndex = rowIndex;

        // PJH - 6/26/2026 - VNEXT-2284 - This flag added because any validation error a Grid may have had in a previous edit session now gets saved to its
        //                                FormInstanceElement.gridValidationMessage property. Setting ignoreAnySavedValidationState to true causes any saved
        //                                error state to be ignored so we only rely on a fresh redo of validation.
        //                                (See corresponding code in GridFormFieldComponent.getValidationErrors())
        // this.gridFormFieldComponent.ignoreAnySavedValidationState = true;

        //VNEXT-863, VNEXT-864 : KLW - There was an issue where CRUD operations on rows during virtualization were throwing errors. This was because
        //we were trying to get rows by their index in the Grid which works for the inital Grid data loaded, but when virtualization kicks in that data
        //is replaced by the 50 rows that show in the Grid. For example a row at index 500 will not be found in a Grid data set that has only 50 items if
        //we search by row index. The solution to this is to use the isPendingEditFor_DatabaseId value for each row where we can find it in whatever
        //Grid data set that has been loaded. If not we do a null check.
        let gridData = sender.data.data;
        let row = null;
        if (databaseId == 0) {
            row = gridData[rowIndex];
        } else {
            row = gridData.find(x => x.__gridRow.databaseId === databaseId);
        }

        if (row) {
            let gridRow: GridRowDef = row.__gridRow;

            gridRow.IsSelected = true;
            this.dataUsedWithKendoGrid.editedRowIndex = rowIndex;
            this.dataUsedWithKendoGrid.selectedGridRow = gridRow;
            this.dataUsedWithKendoGrid.selectedGridRow.GridFlavor = this.flavor;
            this.dataUsedWithKendoGrid.backupForCancel();

            this.createFormGroup();

            sender.editRow(rowIndex, null);
        }
    }

    //***Handles when the user clicks the save icon to "Add" or "Update" a row in the grid. */
    public handleAddOrUpdate({ sender, dataItem, rowIndex, formGroup, isNew }: SaveEvent): void {
        //***Disable the grid's save btn until the event has completed*/
        this.isSaveGridRowButtonDisabled = true;

        const valuesToFieldIds = this.formInstance.getSelectedValuesByFieldId();

        //VNEXT-1540: KLW - Code to set resizable to false
        this.resizable = false;
        let selectedGridRow = this.dataUsedWithKendoGrid.selectedGridRow;

        this.newRowIsBeingAdded = false;
        let cols = sender.columns['_results'];
        this.saving = true;

        this.kendoGridService.addOrUpdateRow(
            this.formInstanceElementId,
            this.formInstanceId,
            this.formFieldId,
            selectedGridRow,
            valuesToFieldIds,
            rowIndex,
            cols,
            dataItem,
            isNew,
            this.gridFormFieldComponent.GridConfig,
            this.GridFormFieldComponent,
            sender.data['total'] // added for VNEXT-1371 in order to set RowIndex for a new row
        ).then(success => {
            if (success) {
                this.flagElementAsUpdated();
                this.extraHeightWhenEditingRow = 0;
                this.closeAnyExistingRowEditor(sender, rowIndex);
                this.gridHasOneUnsavedRow = false;
            }
            this.saving = false;
            this.gridHasBeenEdited = true;
            this.showDoneEditingButton = false;
            dataItem.isEdited = true;

            //***Re-enable save icon*/
            this.isSaveGridRowButtonDisabled = false;
        });
    }

    public handleRowReorder(event: RowReorderEvent) {

        let message = this.gridFlavorImpl.cannotMoveRowMessage(event.draggedRows[0]);
        if (message) {
            alert(message);
            return;
        }

        let originalIndexOfMovedRow = event.draggedRows[0].rowIndex;
        let movedRowId = event.draggedRows[0].dataItem.__gridRow.databaseId;
        let originalIndexOfTargetRow = event.dropTargetRow.rowIndex;
        let targetRowId = event.dropTargetRow.dataItem.__gridRow.databaseId;
        let position = event.dropPosition;

        this.kendoGridService.repositionRow(
            this.flavor,
            this.formInstanceElementId,
            originalIndexOfMovedRow,
            movedRowId,
            originalIndexOfTargetRow,
            targetRowId,
            position
        ).then(x => {
            this.flagDirty();
            this.gridHasBeenEdited = true;
        });
    }

    public handleCancel({ sender, rowIndex }: any): void {
        if (this.gridHasOneUnsavedRow) {
            this.rowCount = 0;
            this.gridHasOneUnsavedRow = false;
            this.gridFormFieldComponent.CheckFieldForValidation();
        }
        this.resizable = false;
        this.extraHeightWhenEditingRow = 0;
        this.flagClean();
        this.closeAnyExistingRowEditor(sender, rowIndex);
    }

    public handleCancelAllEdits(): void {
        this.kendoGridService.clearPending(this.formInstanceElementId).then(x => {
            this.closeAnyExistingRowEditor(this.kendoGridInstance);
            this.flagClean();
            this.rebind();
            this.gridHasBeenEdited = false;
            this.showDoneEditingButton = false;
        });
    }

    public handleDoneEditing(): void {
        this.saveAnyUnsavedRows("handleDoneEditing");
        this.closeAnyExistingRowEditor(this.kendoGridInstance);
        this.showDoneEditingButton = false;
    }

    public handleEnterKeyPressed(): void {
        this.handleDoneEditing();
    }

    // Sets that value of this.detailRowHeight which is used in calculating the grid height
    public handleDetailExpand(event: DetailExpandEvent) {
        // Timeout needed to allow time for the detail panel to be rendered and available
        setTimeout(() => {
            this.detailRowHeight += this.rowDetailPanel?.nativeElement.offsetHeight;
        }, 50);
    }

    public handleDetailCollapse(event: DetailCollapseEvent) {
        this.detailRowHeight = 0;
    }

    public getLockTitleFor(dataItem: any): string {
        if (this.gridFormFieldComponent?.formField?.userCanLockGridRows)
            return dataItem?.isLocked ? 'click to unlock row' : 'click to lock row';
        else
            return dataItem?.isLocked ? 'row is locked' : 'row is open for editing';
    }

    public getLockColor(dataItem: any) {
        return dataItem?.isLocked ? 'darkred' : 'forestgreen';
    }

    public getLockIconFor(dataItem: any): string {
        return dataItem?.isLocked ? 'lock' : 'lock_open';
    }

    public toggleLock($event: any, dataItem: any) {
        let gridRowId = dataItem[KENDO_DATA_GRID_ROW_KEY]?.databaseId;
        if (gridRowId) {
            dataItem.isLocked = !(dataItem?.isLocked);
            this.kendoGridService.lockRow(
                gridRowId,
                dataItem.isLocked
            );
        }
        $event.stopPropagation();
    }

    // Called from within <ng-template kendoGridCellTemplate> which allows the value to be set as innerHTML for HTML escaping
    public getCellDisplayValueForKendo(dataItem: any, columnDef: FormField, iColIndex: number): string {
        let value = dataItem[columnDef.id];
        return value;
    }

    // TODO: Really, this component shouldn't know about field-specific stuff - think about factoring this out of here
    public allParamsSuppliedForConcatenator(dataItem: any, columnDef: any, iColIndex: number): boolean {
        let value = dataItem[columnDef.name];
        return value.indexOf('PARAM-IS-NOT-SET') == -1;
    }

    public openUrl(url: string) {
        window.open(url, '_blank').focus();
    }

    // This gets called when moving/tabbing from one field to another within a row
    // It looks like this exists for Conditional Logic
    public handleTouched(formFieldComponent: FormFieldBaseComponent): void {
        if (!this.formInstance) return;
        let sourceFormInstanceElement: FormInstanceElement = formFieldComponent.FormInstanceElement;

        // Let's make a copy of this form instance that we can manipulate freely (without causing side effects).
        let formInstanceCopy: FormInstance = new FormInstance();
        formInstanceCopy.id = this.formInstance.id;
        formInstanceCopy.form = this.formInstance.form;
        formInstanceCopy.formInstanceElements = [];
        if (this.formInstance.formInstanceElements != null) {
            for (let index: number = 0; index < this.formInstance.formInstanceElements.length; index++) {
                let formInstanceElement: FormInstanceElement = this.formInstance.formInstanceElements[index];
                let fieClone = formInstanceElement.clone();

                if (fieClone.formFieldId == this.gridFormFieldComponent.FormField.id) {
                    fieClone.childGridRows = [];
                    let gridRow: GridRowViewModel = new GridRowViewModel();
                    gridRow.formFieldId = this.gridFormFieldComponent.FormField.id;
                    gridRow.cellDataHash = {};
                    // VNEXT-1505-TODO is this reached? Needs to be by Id
                    //let oldNameBasedCellKeys = Object.keys(this.dataUsedWithKendoGrid.selectedGridRow.HshFormInstanceElementsById);
                    //let values = Object.values(this.dataUsedWithKendoGrid.selectedGridRow.HshFormInstanceElementsById);

                    let cellKeys = this.dataUsedWithKendoGrid.selectedGridRow.GridConfig.ColumnDefs.map(x => `${x.id}`);

                    //console.log('handleTouched()', this.dataUsedWithKendoGrid.selectedGridRow, cellKeys);

                    for (let cellKeyIndex: number = 0; cellKeyIndex < cellKeys.length; cellKeyIndex++) {
                        let cellKey: string = cellKeys[cellKeyIndex];
                        let cellValue: GridFormInstanceElementWrapper = this.dataUsedWithKendoGrid.selectedGridRow.HshFormInstanceElementsById[cellKey];

                        //let idBasedCellValue: GridFormInstanceElementWrapper = values.find(x => { return x.formInstanceElement.formFieldId == cellKey })
                        if (cellValue != null) {
                            let cellValueCopy: IFormContentElement = {
                                id: cellValue.formInstanceElement.id,

                                valueType: cellValue.formInstanceElement.valueType,

                                textValue: cellValue.formInstanceElement.textValue,
                                intValue: cellValue.formInstanceElement.intValue,
                                decimalValue: cellValue.formInstanceElement.decimalValue,
                                booleanValue: cellValue.formInstanceElement.booleanValue,
                                doubleValue: cellValue.formInstanceElement.doubleValue,
                                customValue: cellValue.formInstanceElement.customValue,

                                childFormInstanceElements: []
                            };
                            if (cellKey == formFieldComponent.FormField.name) {
                                cellValueCopy.textValue = sourceFormInstanceElement.textValue;
                                cellValueCopy.intValue = sourceFormInstanceElement.intValue;
                                cellValueCopy.decimalValue = sourceFormInstanceElement.decimalValue;
                                cellValueCopy.booleanValue = sourceFormInstanceElement.booleanValue;
                            }
                            gridRow.cellDataHash[cellKey] = cellValueCopy;
                        }
                    }
                    fieClone.childGridRows.push(gridRow);
                }

                formInstanceCopy.formInstanceElements.push(fieClone);
            }
        }

        // Find my form instance element.
        let myFormField: FormField = this.gridFormFieldComponent.FormField;
        this.fieldConditionalLogicService.sourceFieldValueChanged(formInstanceCopy, myFormField.id).then(response => {
            if ((response != null) && (response.targetFieldInstructions != null)) {
                this.applyFieldConditionalLogicInstructions(response.targetFieldInstructions, false);
            } // if
        });
    }

    public viewInDialog(dataItem: any, columnDef: FormField, iColIndex: number, rowIndex: number): void {
        const text = this.getCellDisplayValueForKendo(dataItem, columnDef, iColIndex);

        this.dialog.open(AlertDialogComponent, {
            data: new AlertDialogModel(`${columnDef.displayName ?? columnDef.name}, Row ${rowIndex + 1}`, text, null, null, true)
        });
    }

    public handleOpenGridCellEditorClicked(eventData: any): void {
        const component: FormFieldBaseComponent = <FormFieldBaseComponent>eventData['component'];
        const gridRowId: number = this.DataUsedWithKendoGrid.selectedGridRow?.DatabaseId;
        const gridConfig: GridConfig = this.gridFormFieldComponent.GridConfig;
        const columnDefs: FormField[] = gridConfig.ColumnDefs;

        const dialogueMgr = new GridCellEditorV2DialogManager(component.FormField, component.FormInstanceElement, columnDefs, gridRowId, this.formInstance);
        const gridCellEditorDialogRef = dialogueMgr.openDialogUsing(this.dialog);

        gridCellEditorDialogRef.afterClosed().subscribe((fie: FormInstanceElement) => {
            if (fie) {
                // this works because event data is passed by reference, so component here is a reference to the component in the grid row itself
                component.FormInstanceElement = fie;
                component.setFormControlValue();
            }
        });
    }

    //VNEXT-894: KLW - This is to manually open a URL in a new tab, otherwise using href will append the current environment url to the url being opened
    public openLink(url: string) {
        if (url != "")
            window.open(url, '_blank').focus();

        return false;
    }

    public getCellDisplayValue(dataItem: any, columnDef: any, iColIndex: number): string {
        let value: string = '';
        if (dataItem?.__gridRow) {
            dataItem = dataItem.__gridRow;
            if ((dataItem.FormInstanceElementWrappers != null) && (iColIndex < dataItem.FormInstanceElementWrappers.length))
                value = dataItem.FormInstanceElementWrappers[iColIndex].standinDisplayValue;
        } else {
            value = dataItem[columnDef.name];
        }

        return value;
    }

    public ButtonLabel(columnDef: FormField): string {
        // TODO: revisit this for ValueCOncatenator Field
        let label = columnDef.transientUrlBuilderButtonLabel?.trim();
        if (label) {
            return label;
        } else {
            return columnDef.displayName ?? columnDef.name;
        }
    }

    public get KendoGridService(): KendoGridService {
        return this.kendoGridService;
    }

    public get DataUsedWithKendoGrid(): DataUsedWithKendoGrid {
        return this.dataUsedWithKendoGrid;
    }

    public get VirtualScrollingPageSize(): number {
        return this.dataUsedWithKendoGrid.gridState.take;
    }

    public get VirtualScrollingSkip(): number {
        let num = this.dataUsedWithKendoGrid.gridState.skip;
        return num;
    }

    public get Saving(): boolean {
        return this.saving;
    }

    public get CanReorderRows(): boolean {
        return this.HasFixedRowHeadings == false &&
            this.UserCanEdit &&
            this.gridFormFieldComponent.formField.userCanReorderGridRows; // Added for VNEXT-2571
    }

    public GridIsInvalid(): boolean {
        return this.gridValidity === FormFieldPropertyEnum.INVALID;
    }

    public get ShowLockRowsColumn(): boolean {
        if (!this.gridFlavorImpl.supportsLockedGridRows) return false;

        return (this.gridFormFieldComponent?.formField?.showLockGridRows && !this.gridFormFieldComponent?.formField?.readOnly);
    }

    public get UserCanLockGridRows(): boolean {
        return (this.UserCanEdit && this.gridFormFieldComponent?.formField?.userCanLockGridRows);
    }

    //VNEXT-980: KLW - Property to set the number of Kendo grid rows to display
    public GetGridHeight() {
        let rowCount = this.kendoGridService?.DataResult?.data?.length;
        let rowsDisplayed: number = this.gridFormFieldComponent.formField.GetDisplayKendoGridRows;

        let retVal = GridFieldEditorComponent.CalculateGridHeight(this.rowHeight,
            this.toolbarHeadersFilters,
            this.detailRowHeight,
            this.extraHeightWhenEditingRow,
            rowCount,
            rowsDisplayed,
            true, // Wrapped header text is enabled.
            this.ShowNumericTotalsFooter);

        return retVal;
    }

    public get GetGridRowinfo() {
        let rowCount = this.kendoGridService?.DataResult?.total;
        if (!rowCount || rowCount == 0) return '';
        return rowCount + (rowCount > 1 ? ' rows' : ' row');
    }

    // At the point where this is called, individual invalid fields within the grid will be reporting their problems
    // BUT the grid as a whole has not yet been flagged as invalid. That's what this method is doing.
    // By returning 'invalid' (if any single field is invalid) we allow later event handling to grey out the save button
    public handleValidityChange(state: string) {
        // We should be able to rely on FormGroup.valid to but that's not working correctly.
        // So this workaround is used where we iterate through all controls and if one is invalid then return invalid, otherwise return valid.
        if (this.formGroup) { // HOW DOES FORM GROUP GET BUILT?
            let isValid = FormFieldPropertyEnum.VALID; // BETTER NAME?

            for (const field in this.formGroup.controls) { // 'field' is a string

                const control = this.formGroup.get(field); // 'control' is a FormControl  

                if (!control?.valid) {
                    isValid = FormFieldPropertyEnum.INVALID;
                    break;
                }
            }
            this.editorGridValidation.emit(isValid);
        }
    }

    public handleCellClose(event: any) {
        // NOOP
    }

    private closeAnyExistingRowEditor(grid: GridComponent, rowIndex = this.dataUsedWithKendoGrid.editedRowIndex): void {
        if (grid != null) {
            grid.closeRow(-1);
            grid.closeRow(rowIndex);
        }
        this.childComponentsRequiringIndividualValidtional = [];
        this.rowEditInProgress = false;
    }

    private flagDirty() {
        this.gridFormFieldComponent.FormInstanceElement.UserUpdatedData = true;
        this.gridFormFieldComponent.FormInstanceElement.gridRowsValueHasChanged = true;
        // Make sure components that wrap this one get protected from accidental data loss - see  https://community-dc.max.gov/x/MI4ynw for details
        this.gridFormFieldComponent.flaggedAsDirty.emit();
    }
    private flagClean() {
        this.gridFormFieldComponent.FormInstanceElement.UserUpdatedData = false;
        this.gridFormFieldComponent.FormInstanceElement.gridRowsValueHasChanged = false;
        // Make sure components that wrap this one get protected from accidental data loss - see  https://community-dc.max.gov/x/MI4ynw for details
        this.gridFormFieldComponent.flaggedAsClean.emit();
    }
    public gridIsDirty() {
        return this.gridFormFieldComponent.FormInstanceElement.UserUpdatedData;
    }

    public get RowEditInProgress(): boolean {
        return this.rowEditInProgress;
    }

    public get FormFieldId(): number {
        return this.formFieldId;
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ END STUFF ADDED FOR KENDO GRID ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

    public get FieldDefinitionService() {
        return this.fieldDefinitionService;
    }

    public get GridFormFieldComponent() {
        return this.gridFormFieldComponent;
    }

    public get FormInstanceElementId() {
        return this.formInstanceElementId;
    }

    // Begin called by my HTML code.
    public ColumnIsSticky(colName: string): boolean {
        return this.gridFormFieldComponent.ColumnIsSticky(colName);
    }

    public GridColumnDisplayName(columnDef: FormField): string {
        let strDisplayName: string = columnDef.name;

        if ((columnDef !== undefined) && (columnDef !== null) && (columnDef.displayName !== null) && (columnDef.displayName.trim() !== ''))
            strDisplayName = columnDef.displayName;

        return strDisplayName;
    }

    public getFormInstanceElementWrapper(hshColumnDef: FormField, gridRow: GridRowDef): GridFormInstanceElementWrapper[] {
        let wrapper: GridFormInstanceElementWrapper = gridRow.getFormInstanceElementWrapper(hshColumnDef);

        if (wrapper && wrapper.formInstanceElement) {
            if (!wrapper.formInstanceElement.transientValuesHash) {
                wrapper.formInstanceElement.transientValuesHash = {};
            }
            wrapper.formInstanceElement.transientValuesHash[GRID_ROW_ID_KEY] = gridRow.ClientId;
        } else if (!wrapper) {
            let errorMsg: string = "GridFormFieldComponent.getFormInstanceElementWrapper():  cannot get a form instance element wrapper.";
            super.raiseException(errorMsg);
        } else {
            let errorMsg: string = "GridFormFieldComponent.getFormInstanceElementWrapper():  cannot get a form instance element.";
            super.raiseException(errorMsg);
        }
        let arrWrapper: GridFormInstanceElementWrapper[] = [wrapper];

        return arrWrapper;
    }

    public ignoredNgModelChange(columnDef: FormField, value: FormInstanceElement): void {
        // Note:  this method is a NOOP by design.
    }
    // Note:  method controlValueChanged(), next, must be defined as an arrow function.
    public gridColumnValueChanged(columnDef: FormField, value: FormInstanceElement): void {

        if (value != null) {
            this.flagDirty();

            let totalsHelper: GridColumnTotalsHelper = <GridColumnTotalsHelper>this.helper.DataSpy;
            if (totalsHelper != null)
                totalsHelper.cellValueChanged(this.dataUsedWithKendoGrid.editedRowIndex, columnDef, value);
        }
    }

    public handleMimickedNgModelChange(eventData: any): void {
        let formField: FormField = eventData['formField'];
        let formInstanceElement: FormInstanceElement = eventData['formInstanceElement'];

        // We need to reproduce the work of [(ngModel)] and then the call from the (ngModelChange) event.
        let columnDefs: FormField[] = this.GridColumnDefs;
        let iColIndex: number = 0
        let columnDef: FormField = null;
        let columnFound: boolean = false;
        for (; iColIndex < columnDefs.length; iColIndex++) {
            columnDef = columnDefs[iColIndex];
            if (columnDef.name == formField.name) {
                columnFound = true;
                break;
            }
        }

        if (columnFound) {
            let formInstanceElementWrapper: GridFormInstanceElementWrapper = this.DataUsedWithKendoGrid.selectedGridRow?.FormInstanceElementWrappers[iColIndex];
            if (formInstanceElementWrapper != null) {
                formInstanceElementWrapper.formInstanceElement.copyWithAnyChildElements(formInstanceElement);

                this.gridColumnValueChanged(columnDef, formInstanceElementWrapper.formInstanceElement);
            }
        }
    }

    public getCellNameFor(row: number, col: number): string {
        return `t-${row}-${col}`;
    }
    // End handling MatPaginator events.

    // Helper methods.
    private jobCompletedOrUpdatedArrowFunction = (asyncJob: AsyncJob): void => {
        // TO DO:  IMPLEMENT THIS METHOD.
    }

    private hideGridBodyIfSoConfigured(): void {
        let gridElement = $(this).find('kendo-grid');
        if (gridElement != null) {
            let gridBodyElement = $(gridElement).find("[aria-label='Data table']");
            if (gridBodyElement != null) {
                gridBodyElement.remove();
            }
        }
    }

    //VNEXT-1540: KLW - Code to auto resize the columns when editing
    //https://www.telerik.com/kendo-angular-ui/components/grid/columns/resizing#auto-fitting-content
    //For this to work, you MUST have the grid attribute of "resizable" (https://www.telerik.com/kendo-angular-ui/components/grid/api/gridcomponent#resizable) set to true
    private fitColumns(): void {
        // this.ngZone.onStable
        //     .asObservable()
        //     .pipe(take(1))
        //     .subscribe(() => {
        //         this.kendoGridInstance.autoFitColumns();
        //     });
    }
}
